import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  DoffinNoticeStatus,
  EFormType,
  PublicationChannel,
  PublicationDto,
  PublicationFailureDto,
  PublicationStatus,
  SourcingEventStatus,
} from "../../autogen/bff-api";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { schemeEnding } from "./result-notice/schemeEnding";

interface Props {
  status: SourcingEventStatus;
  publications?: PublicationDto[];
}

export const PublicationStatusBanner = ({ status, publications = [] }: Props) => {
  const { t } = useTranslation();

  if (status === SourcingEventStatus.PublicationPending) {
    return (
      <Box mt="4">
        <Alert status="info" rounded="md" fontSize="sm" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle>{t("Sent to Doffin – awaiting publication")}</AlertTitle>
            <AlertDescription>
              {t(
                "Your notice will be published as soon as Doffin has processed it. There's nothing you need to do — this page updates automatically once it's live.",
              )}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (status === SourcingEventStatus.PublicationFailed) {
    const failure = contractNoticeFailure(publications);
    return (
      <Box mt="4">
        <Alert status="error" rounded="md" fontSize="sm" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle>{t("Publication to Doffin failed")}</AlertTitle>
            <AlertDescription>
              {failure ? (
                <DoffinFailureReason failure={failure} />
              ) : (
                t(
                  "Something went wrong while publishing this notice to Doffin. This is rare — please contact support and we'll help you get it published.",
                )
              )}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  const ending = status === SourcingEventStatus.Published ? schemeEnding(publications) : { state: "none" as const };
  if (ending.state === "pending") {
    return (
      <Box mt="4">
        <Alert status="info" rounded="md" fontSize="sm" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle>{t("The ending is sent to Doffin – awaiting publication")}</AlertTitle>
            <AlertDescription>
              {t("The qualification system ends as soon as Doffin has published the notice. Until then it stays open.")}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }
  if (ending.state === "refused") {
    return (
      <Box mt="4">
        <Alert status="error" rounded="md" fontSize="sm" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle>{t("Doffin did not publish the ending")}</AlertTitle>
            <AlertDescription>
              {ending.failure && <DoffinFailureReason failure={ending.failure} />}
              <Text mt="1">
                {t("The qualification system is still open. You can send the ending again from the menu.")}
              </Text>
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return null;
};

const DoffinFailureReason = ({ failure }: { failure: PublicationFailureDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const date = displayDate(failure.reportedAt);
  const what = (() => {
    switch (failure.doffinStatus) {
      case DoffinNoticeStatus.Stopped:
        return t("Doffin stopped the notice on {{date}}.", { date });
      case DoffinNoticeStatus.Deleted:
        return t("Doffin deleted the notice on {{date}}.", { date });
      case DoffinNoticeStatus.Archived:
        return t("Doffin archived the notice on {{date}}, before it was published.", { date });
      default:
        return t("Doffin marked the notice as not published on {{date}}.", { date });
    }
  })();
  return (
    <>
      <Text>{what}</Text>
      <Text>{t("Doffin gave no further reason. Contact support and we'll help you get it published.")}</Text>
    </>
  );
};

const contractNoticeFailure = (publications: PublicationDto[]): PublicationFailureDto | undefined =>
  publications
    .filter(
      (p) =>
        p.channel === PublicationChannel.Doffin &&
        p.eFormType === EFormType.Cn16 &&
        p.noticeVersion === "01" &&
        p.status === PublicationStatus.Failed,
    )
    .map((p) => p.failure)
    .filter((failure): failure is PublicationFailureDto => failure != null)
    .at(-1);
